import { useState, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

interface Location {
  id: string;
  name: string;
  description: string;
  lat: number;
  lng: number;
  categoryId: string;
  address?: string;
  photos?: string[];
  dateVisited?: string;
  rating?: number;
  notes?: string;
}

interface MapConfig {
  center: { lat: number; lng: number };
  zoom: number;
}

export const useGoogleMaps = () => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentMarker, setCurrentMarker] = useState<google.maps.Marker | null>(null);
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const mapRef = useRef<HTMLDivElement>(null);

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Debug mejorado
  useEffect(() => {
    console.log('🔍 Debug Google Maps API Key:');
    console.log('- API Key exists:', !!API_KEY);
    console.log('- API Key length:', API_KEY?.length || 0);
    console.log('- API Key preview:', API_KEY ? `${API_KEY.substring(0, 10)}...${API_KEY.substring(API_KEY.length - 5)}` : 'NO ENCONTRADA');
    console.log('- Environment:', process.env.NODE_ENV);
    
    // Verificar todas las variables de entorno que empiecen con REACT_APP_
    const reactAppEnvs = Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'));
    console.log('- Variables REACT_APP_ disponibles:', reactAppEnvs);
  }, [API_KEY]);

  // Configuración por defecto (Monterrey, MX)
  const defaultConfig: MapConfig = {
    center: { lat: 25.6866, lng: -100.3161 },
    zoom: 12
  };

  // Validar API Key antes de cargar
  useEffect(() => {
    if (!API_KEY) {
      setError('❌ Google Maps API Key no encontrada. Verifica tu archivo .env');
      setIsLoading(false);
      return;
    }

    if (API_KEY.length < 30) {
      setError('❌ La API Key parece estar incompleta. Verifica que sea correcta');
      setIsLoading(false);
      return;
    }

    if (!API_KEY.startsWith('AIza')) {
      setError('❌ La API Key no tiene el formato correcto (debe empezar con "AIza")');
      setIsLoading(false);
      return;
    }

    // Continuar con la carga si la API Key parece válida
    loadGoogleMapsAPI();
  }, [API_KEY]);

  const loadGoogleMapsAPI = () => {
    const loader = new Loader({
      apiKey: API_KEY!,
      version: 'weekly',
      libraries: ['places', 'geometry']
    });

    console.log('⏳ Iniciando carga de Google Maps API...');

    loader.load()
      .then(() => {
        console.log('✅ Google Maps API cargada exitosamente');
        setIsLoaded(true);
        setError(null);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('❌ Error cargando Google Maps API:', err);
        
        // Errores específicos más descriptivos
        let errorMessage = 'Error cargando Google Maps';
        
        if (err.message?.includes('InvalidKeyMapError')) {
          errorMessage = '🔑 API Key inválida. Verifica que la key sea correcta y esté habilitada';
        } else if (err.message?.includes('RequestDenied')) {
          errorMessage = '🚫 Solicitud denegada. Verifica que tengas habilitada la Maps JavaScript API';
        } else if (err.message?.includes('QuotaExceeded')) {
          errorMessage = '📊 Cuota excedida. Verifica tu facturación en Google Cloud Console';
        } else if (err.message?.includes('RefererNotAllowed')) {
          errorMessage = '🌐 Dominio no permitido. Configura las restricciones en Google Cloud Console';
        }
        
        setError(`${errorMessage}: ${err.message}`);
        setIsLoading(false);
      });
  };

  // Crear el mapa cuando la API esté lista
  useEffect(() => {
    if (isLoaded && mapRef.current && !map) {
      try {
        console.log('🗺️ Inicializando mapa...');
        
        const newMap = new google.maps.Map(mapRef.current, {
          center: defaultConfig.center,
          zoom: defaultConfig.zoom,
          styles: [
            {
              featureType: 'all',
              elementType: 'geometry.fill',
              stylers: [{ color: '#f0f5ff' }]
            },
            {
              featureType: 'water',
              elementType: 'geometry',
              stylers: [{ color: '#aeceff' }]
            }
          ],
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: 'greedy' // Mejora la experiencia móvil
        });

        // Crear InfoWindow
        const newInfoWindow = new google.maps.InfoWindow();
        
        setMap(newMap);
        setInfoWindow(newInfoWindow);
        console.log('✅ Mapa inicializado exitosamente');
      } catch (err) {
        console.error('❌ Error creando mapa:', err);
        setError('Error inicializando el mapa');
      }
    }
  }, [isLoaded, map]);

  // Función para mostrar una ubicación en el mapa
  const showLocation = (location: Location) => {
    if (!map || !infoWindow) {
      console.warn('⚠️ Mapa no está listo');
      return;
    }

    try {
      // Remover marcador anterior
      if (currentMarker) {
        currentMarker.setMap(null);
      }

      // Crear nuevo marcador
      const marker = new google.maps.Marker({
        position: { lat: location.lat, lng: location.lng },
        map: map,
        title: location.name,
        animation: google.maps.Animation.DROP,
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new google.maps.Size(40, 40)
        }
      });

      // Contenido del InfoWindow mejorado
      const infoContent = `
        <div style="max-width: 300px; padding: 10px; font-family: system-ui, -apple-system, sans-serif;">
          <h3 style="margin: 0 0 10px 0; color: #1b34a1; font-weight: bold; font-size: 16px;">
            ${location.name}
          </h3>
          <p style="margin: 0 0 8px 0; color: #666; font-size: 14px; line-height: 1.4;">
            ${location.description}
          </p>
          ${location.address ? `
            <p style="margin: 0 0 8px 0; color: #888; font-size: 12px;">
              📍 ${location.address}
            </p>
          ` : ''}
          ${location.dateVisited ? `
            <p style="margin: 0 0 8px 0; color: #888; font-size: 12px;">
              📅 Visitado: ${new Date(location.dateVisited).toLocaleDateString('es-ES')}
            </p>
          ` : ''}
          ${location.rating ? `
            <p style="margin: 0 0 8px 0; color: #888; font-size: 12px;">
              ⭐ ${location.rating}/5
            </p>
          ` : ''}
          ${location.notes ? `
            <p style="margin: 0 0 10px 0; color: #666; font-size: 13px; font-style: italic; line-height: 1.3;">
              "${location.notes}"
            </p>
          ` : ''}
          <a href="https://www.google.com/maps?q=${location.lat},${location.lng}" 
             target="_blank" 
             rel="noopener noreferrer"
             style="display: inline-block; padding: 8px 16px; background: #0a6ecd; color: white; text-decoration: none; border-radius: 6px; font-size: 12px; margin-top: 5px; transition: background 0.2s;">
            🗺️ Abrir en Google Maps
          </a>
        </div>
      `;

      // Mostrar InfoWindow
      infoWindow.setContent(infoContent);
      infoWindow.open(map, marker);

      // Centrar mapa en la ubicación con animación suave
      map.panTo({ lat: location.lat, lng: location.lng });
      map.setZoom(15);

      // Guardar referencia del marcador
      setCurrentMarker(marker);

      // Event listener para el marcador
      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      console.log('✅ Ubicación mostrada:', location.name);
    } catch (err) {
      console.error('❌ Error mostrando ubicación:', err);
      setError('Error mostrando la ubicación');
    }
  };

  // Función para resetear el mapa a la vista por defecto
  const resetMapView = () => {
    if (!map) return;

    if (currentMarker) {
      currentMarker.setMap(null);
      setCurrentMarker(null);
    }

    if (infoWindow) {
      infoWindow.close();
    }

    map.panTo(defaultConfig.center);
    map.setZoom(defaultConfig.zoom);
  };

  // Función para obtener coordenadas de una dirección
  const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
    if (!isLoaded) {
      console.warn('⚠️ Google Maps no está cargado');
      return null;
    }

    try {
      const geocoder = new google.maps.Geocoder();
      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            resolve(results);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });

      const location = result[0].geometry.location;
      return {
        lat: location.lat(),
        lng: location.lng()
      };
    } catch (err) {
      console.error('❌ Error en geocoding:', err);
      return null;
    }
  };

  return {
    mapRef,
    map,
    isLoaded,
    isLoading,
    error,
    showLocation,
    resetMapView,
    geocodeAddress,
    currentLocation: currentMarker ? {
      lat: currentMarker.getPosition()?.lat() || 0,
      lng: currentMarker.getPosition()?.lng() || 0
    } : null
  };
};